import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { socialPostApi } from '../../api/socialPostApi';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import SafeImage from '../../components/SafeImage';
import PostEngagementBar from './PostEngagementBar';
import PostMediaGallery from './PostMediaGallery';
import SocialQuoteReferenceCard from './SocialQuoteReferenceCard';
import DirectMessageButton from '../messages/DirectMessageButton';
import SocialPostComments from './SocialPostComments';
import ContentReportDialog from './ContentReportDialog';
import { bumpFollowingFeedEpoch } from '../social-following/followingFeedRefresh';
import {
  canChangePostVisibility,
  canDeletePost,
  canRepublishPost,
  canUnpublishPost,
  isSocialPostOwner,
  normalizeSocialPostDetail,
  socialPostErrorMessage,
} from './socialPostDetailModel';
import { socialPostKindProductLabel } from './socialPostKindPresentation';
import { SOCIAL_ACTION_PRODUCT_CHROME as socialChrome } from './socialActionPresentation';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { travelSafetyApi, newSafetyClientRequestId, safetyErrorMessage } from '../../api/travelSafetyApi';
import './social-post.css';
import '../messages/travel-messages.css';

const formatTime = (value) => value && !Number.isNaN(new Date(value).getTime())
  ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '';

const FOLLOW_FAILED = '無法更新追蹤狀態，請稍後再試。';

export default function SocialPostPage() {
  const { postId } = useParams();
  const requestPostId = postId;
  const { status, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [detail, setDetail] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [actionError, setActionError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmBlockAuthor, setConfirmBlockAuthor] = useState(false);
  const generation = useRef(0);
  const commentsRef = useRef(null);
  const commentsIntent = useRef(false);
  const focusComments = useCallback(() => {
    const section = commentsRef.current;
    if (!section) return;
    commentsIntent.current = true;
    const target = section.querySelector('textarea:not(:disabled), .comments__signin') || section;
    target.focus({ preventScroll: true });
    target.scrollIntoView?.({ block: 'center', behavior: 'instant' });
  }, []);
  useEffect(() => {
    commentsIntent.current = false;
  }, [requestPostId]);
  useEffect(() => {
    if (phase === 'ready' && location.hash === '#comments') focusComments();
  }, [phase, location.hash, focusComments]);
  useEffect(() => {
    const section = commentsRef.current;
    if (phase !== 'ready' || !section || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (commentsIntent.current && section.contains(document.activeElement)) {
        document.activeElement.scrollIntoView?.({ block: 'center', behavior: 'instant' });
      }
    });
    observer.observe(section.parentElement);
    return () => observer.disconnect();
  }, [phase, requestPostId]);

  async function load() {
    const requestGeneration = ++generation.current;
    if (!/^[1-9]\d*$/.test(requestPostId || '')) { setPhase('not-found'); return; }
    setPhase('loading'); setError(''); setDetail(null); setActionError('');
    setConfirmDelete(false); setConfirmBlockAuthor(false);
    try {
      const value = normalizeSocialPostDetail(await socialPostApi.getDetail(requestPostId));
      if (requestGeneration !== generation.current) return;
      setDetail(value); setPhase('ready');
    }
    catch (cause) {
      if (requestGeneration !== generation.current) return;
      const notFound = cause?.raw?.key === 'travel.social.post.not.found' || cause?.raw?.code === 95702;
      if (notFound) setPhase('not-found');
      else {
        setError(resolveSafeUserVisibleError(cause, (key) => key, 'travel.common.loadFailed'));
        setPhase('error');
      }
    }
  }

  useEffect(() => { if (status !== 'unknown') load(); return () => { generation.current += 1; }; }, [requestPostId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  function requireAuth() {
    if (isAuthenticated) return true;
    navigate('/login', { state: { from: authReturnLocation(location) } }); return false;
  }

  function onEngagementChange(_postId, property, active) {
    setDetail((current) => {
      if (!current?.engagement) return current;
      const countKey = property === 'liked' ? 'likeCount' : 'saveCount';
      const wasActive = Boolean(current.engagement[property]);
      let delta = 0;
      if (active && !wasActive) delta = 1;
      if (!active && wasActive) delta = -1;
      return {
        ...current,
        engagement: {
          ...current.engagement,
          [property]: active,
          [countKey]: Math.max(0, current.engagement[countKey] + delta),
        },
      };
    });
  }

  async function toggleFollow() {
    if (!requireAuth() || busy) return;
    const following = detail.engagement.followingAuthor;
    setBusy('follow'); setActionError('');
    try {
      await (following ? socialPostApi.unfollow(detail.author.memberId) : socialPostApi.follow(detail.author.memberId));
      bumpFollowingFeedEpoch();
      setDetail((current) => ({ ...current, engagement: { ...current.engagement, followingAuthor: !following } }));
    } catch { setActionError(FOLLOW_FAILED); }
    finally { setBusy(''); }
  }

  async function handleVisibilityChange(nextVisibility) {
    if (!requireAuth() || busy || nextVisibility === detail?.visibility) return;
    setBusy('visibility');
    setActionError('');
    try {
      const result = await socialPostApi.changeVisibility(detail.postId, {
        visibility: nextVisibility,
        expectedRowVersion: detail.rowVersion,
      });
      const nextVersion = Number(result?.rowVersion) || (detail.rowVersion + 1);
      setDetail((current) => ({
        ...current,
        visibility: result?.visibility || nextVisibility,
        rowVersion: Math.max(current?.rowVersion || 0, nextVersion),
      }));
      try {
        const fresh = normalizeSocialPostDetail(await socialPostApi.getDetail(requestPostId));
        setDetail((current) => ({
          ...current,
          ...fresh,
          visibility: fresh.visibility || nextVisibility,
          rowVersion: Math.max(nextVersion, fresh.rowVersion || 0),
        }));
      } catch {}
      bumpFollowingFeedEpoch();
    } catch (cause) {
      setActionError(socialPostErrorMessage(cause));
    } finally {
      setBusy('');
    }
  }

  async function handleUnpublish() {
    if (!requireAuth() || busy) return;
    setBusy('unpublish');
    setActionError('');
    try {
      const result = await socialPostApi.unpublish(detail.postId, {
        expectedRowVersion: detail.rowVersion,
      });
      const nextVersion = Number(result?.rowVersion) || (detail.rowVersion + 1);
      setDetail((current) => ({
        ...current,
        status: 'UNPUBLISHED',
        rowVersion: Math.max(current?.rowVersion || 0, nextVersion),
      }));
      try {
        const fresh = normalizeSocialPostDetail(await socialPostApi.getDetail(requestPostId));
        setDetail((current) => ({
          ...current,
          ...fresh,
          status: fresh.status || 'UNPUBLISHED',
          rowVersion: Math.max(nextVersion, fresh.rowVersion || 0),
        }));
      } catch {}
      bumpFollowingFeedEpoch();
    } catch (cause) {
      setActionError(socialPostErrorMessage(cause));
    } finally {
      setBusy('');
    }
  }

  async function handleRepublish() {
    if (!requireAuth() || busy) return;
    setBusy('republish');
    setActionError('');
    try {
      const result = await (socialPostApi.republish || socialPostApi.publish)(detail.postId, {
        expectedRowVersion: detail.rowVersion,
      });
      const nextVersion = Number(result?.rowVersion) || (detail.rowVersion + 1);
      setDetail((current) => ({
        ...current,
        status: 'PUBLISHED',
        rowVersion: Math.max(current?.rowVersion || 0, nextVersion),
      }));
      try {
        const fresh = normalizeSocialPostDetail(await socialPostApi.getDetail(requestPostId));
        setDetail((current) => ({
          ...current,
          ...fresh,
          status: fresh.status || 'PUBLISHED',
          rowVersion: Math.max(nextVersion, fresh.rowVersion || 0),
        }));
      } catch {}
      bumpFollowingFeedEpoch();
    } catch (cause) {
      setActionError(socialPostErrorMessage(cause));
    } finally {
      setBusy('');
    }
  }

  async function handleDelete() {
    if (!requireAuth() || busy) return;
    setBusy('delete');
    setActionError('');
    try {
      await socialPostApi.delete(detail.postId, {
        expectedRowVersion: detail.rowVersion,
      });
      bumpFollowingFeedEpoch();
      navigate('/explore', { replace: true });
    } catch (cause) {
      const notFound = cause?.raw?.key === 'travel.social.post.not.found' || cause?.raw?.code === 95702 || cause?.httpStatus === 404;
      if (notFound) {
        setPhase('not-found');
      } else {
        setActionError(socialPostErrorMessage(cause));
      }
      setBusy('');
      setConfirmDelete(false);
    }
  }

  async function handleBlockAuthor() {
    if (!requireAuth() || busy || !detail?.author?.memberId) return;
    setBusy('block');
    setActionError('');
    try {
      await travelSafetyApi.blockMember(
        String(detail.author.memberId),
        newSafetyClientRequestId('block'),
      );
      bumpFollowingFeedEpoch();
      setConfirmBlockAuthor(false);
      navigate('/explore', { replace: true });
    } catch (cause) {
      setActionError(safetyErrorMessage(cause, (key) => key, '封鎖作者失敗，請稍後再試。'));
      setConfirmBlockAuthor(false);
    } finally {
      setBusy('');
    }
  }

  if (status === 'unknown' || phase === 'loading') return <main className="social-detail-state" aria-live="polite"><div className="explore-spinner" /><p>{socialChrome.loadingStory}</p></main>;
  if (phase === 'not-found') return <main className="social-detail-state"><h1>{socialChrome.postUnavailableTitle}</h1><p>{socialChrome.postUnavailableBody}</p><Link to="/explore">{socialChrome.backToExplore}</Link></main>;
  if (phase === 'error') return <main className="social-detail-state" role="alert"><h1>{socialChrome.loadStoryFailedTitle}</h1><p>{error}</p><button type="button" onClick={load}>{socialChrome.tryAgain}</button></main>;

  const ownPost = isAuthenticated && String(user?.memberId || user?.id) === String(detail.author.memberId);
  const canManage = isAuthenticated && isSocialPostOwner(detail, user?.memberId || user?.id);
  const backTo = location.state?.from?.pathname || '/explore';
  const backLabel = String(backTo).startsWith('/social/following') ? socialChrome.backToFollowing : socialChrome.backToExplore;
  return <div className="social-detail-layout">
    <main className="social-detail-main">
      <article className="social-detail">
        <header className="social-detail__header">
          <Link className="social-detail__author" to={`/social/creators/${detail.author.memberId}`}>
            <SafeImage className="social-detail__avatar" src={detail.author.avatarUrl} alt="" />
            <span><strong>{detail.author.displayName}</strong><time dateTime={detail.publishedAt || undefined}>{formatTime(detail.publishedAt)}</time></span>
          </Link>
          {!ownPost ? (
            <div className="social-detail__header-actions">
              <button
                className="social-detail__follow"
                type="button"
                aria-label={detail.engagement.followingAuthor ? socialChrome.following : socialChrome.follow}
                aria-pressed={detail.engagement.followingAuthor}
                disabled={Boolean(busy)}
                onClick={toggleFollow}
              >
                {detail.engagement.followingAuthor ? socialChrome.following : socialChrome.follow}
              </button>
              <DirectMessageButton className="social-detail__message" peerMemberId={detail.author.memberId} label="傳訊息給作者" />
              <button
                type="button"
                className="social-detail__report"
                data-testid="social-post-report"
                disabled={Boolean(busy)}
                onClick={() => {
                  if (!requireAuth()) return;
                  setReportOpen(true);
                }}
              >
                檢舉
              </button>
              {isAuthenticated && detail?.author?.memberId ? (
                confirmBlockAuthor ? (
                  <div className="social-detail__block-confirm" role="dialog" aria-label="封鎖作者">
                    <p role="status">確定要封鎖此作者嗎？封鎖後將不會再看到此作者的貼文與互動。</p>
                    <button
                      type="button"
                      className="social-detail__danger-btn"
                      disabled={Boolean(busy)}
                      onClick={handleBlockAuthor}
                    >
                      {busy === 'block' ? '封鎖中…' : '確認封鎖'}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => setConfirmBlockAuthor(false)}
                    >
                      取消
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="social-detail__block"
                    data-testid="social-post-block-author"
                    disabled={Boolean(busy)}
                    onClick={() => setConfirmBlockAuthor(true)}
                  >
                    封鎖作者
                  </button>
                )
              ) : null}
            </div>
          ) : null}
        </header>
        <div className="social-detail__meta"><span>{socialPostKindProductLabel(detail.postKind)}</span>{detail.editedAt ? <span>Edited {formatTime(detail.editedAt)}</span> : null}</div>
        {detail.text ? <p className="social-detail__body">{detail.text}</p> : null}
        <PostMediaGallery media={detail.media} alt={socialChrome.coverAlt(detail.author.displayName)} />
        {ownPost ? (
          <div className="social-detail__owner-policies">
            <p className="social-detail__policy-line">誰可以回覆：{detail.replyPolicyLabel}</p>
            <p className="social-detail__policy-line">引用：{detail.quotePolicyLabel}</p>
            <p><Link to={`/social/posts/${detail.postId}/edit`}>編輯貼文</Link></p>
          </div>
        ) : null}
        {canManage ? (
          <div className="social-detail__owner-lifecycle" data-testid="social-post-owner-lifecycle">
            {canChangePostVisibility(detail, user?.memberId || user?.id) ? (
              <div className="social-detail__visibility-control">
                <label htmlFor="social-post-visibility">{socialChrome.visibilityLabel}</label>
                <select
                  id="social-post-visibility"
                  aria-label={socialChrome.visibility}
                  value={detail.visibility || 'PUBLIC'}
                  disabled={Boolean(busy)}
                  onChange={(e) => handleVisibilityChange(e.target.value)}
                >
                  <option value="PUBLIC">公開</option>
                  <option value="FOLLOWERS">追蹤者</option>
                  <option value="PRIVATE">僅自己</option>
                </select>
              </div>
            ) : null}
            <div className="social-detail__lifecycle-actions">
              {canUnpublishPost(detail, user?.memberId || user?.id) ? (
                <button
                  type="button"
                  className="social-detail__unpublish-btn"
                  disabled={Boolean(busy)}
                  onClick={handleUnpublish}
                >
                  {busy === 'unpublish' ? socialChrome.unpublishing : socialChrome.unpublish}
                </button>
              ) : null}
              {canRepublishPost(detail, user?.memberId || user?.id) ? (
                <button
                  type="button"
                  className="social-detail__republish-btn"
                  disabled={Boolean(busy)}
                  onClick={handleRepublish}
                >
                  {busy === 'republish' ? socialChrome.republishing : socialChrome.republish}
                </button>
              ) : null}
              {canDeletePost(detail, user?.memberId || user?.id) ? (
                confirmDelete ? (
                  <div className="social-detail__delete-confirm" role="dialog" aria-label={socialChrome.confirmDelete}>
                    <p role="status">{socialChrome.deletePrompt}</p>
                    <button
                      type="button"
                      className="social-detail__danger-btn"
                      disabled={Boolean(busy)}
                      aria-label={socialChrome.confirmDelete}
                      onClick={handleDelete}
                    >
                      {busy === 'delete' ? socialChrome.deleting : socialChrome.confirmDelete}
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => setConfirmDelete(false)}
                    >
                      {socialChrome.cancel}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="social-detail__danger-btn"
                    disabled={Boolean(busy)}
                    aria-label={socialChrome.delete}
                    onClick={() => setConfirmDelete(true)}
                  >
                    {socialChrome.delete}
                  </button>
                )
              ) : null}
            </div>
          </div>
        ) : null}
        {detail.location ? <div className="social-detail__location"><span aria-hidden="true">⌖</span><span><strong>{detail.location.displayName}</strong><small>{[detail.location.locationType, detail.location.countryCode].filter(Boolean).join(' · ')}</small></span></div> : null}
        {detail.journey ? (
          <Link className="social-detail__journey social-detail__journey--compact" to={`/public/journeys/${detail.journey.publicationId}`}>
            <span>
              <small>公開行程</small>
              <strong>{detail.journey.title}</strong>
              {detail.journey.summary ? <em>{detail.journey.summary}</em> : null}
              <em>查看 ›</em>
            </span>
          </Link>
        ) : detail.journeyUnavailable ? (
          <div className="social-detail__journey social-detail__journey--unavailable">
            <span>
              <small>公開行程</small>
              <strong>{detail.journeyUnavailable.title || '行程已下架'}</strong>
              <em>附件無法開啟，貼文與圖片仍保留</em>
            </span>
          </div>
        ) : null}
        {detail.quote ? (
          detail.quote.available ? (
            <Link className="social-detail__quote social-detail__quote--compact" to={`/social/posts/${detail.quote.postId}`}>
              <SocialQuoteReferenceCard quote={detail.quote} />
            </Link>
          ) : (
            <div className="social-detail__quote social-detail__quote--unavailable">
              <strong>無法顯示被引用的貼文</strong>
              <p>來源可能已限制或不再公開。</p>
            </div>
          )
        ) : null}
        <PostEngagementBar
          postId={detail.postId}
          engagement={detail.engagement}
          detailTo={`/social/posts/${detail.postId}`}
          commentHref="#comments"
          onOpenComments={focusComments}
          shareTitle={detail.text || 'Travel story'}
          onEngagementChange={onEngagementChange}
          quoteAllowed={detail.quotePolicy !== 'DISALLOW'}
          className="social-detail__engagement"
        />
        {actionError ? <p className="social-detail__inline-error" role="alert">{actionError}</p> : null}
      </article>
      {reportOpen ? (
        <ContentReportDialog
          open
          targetType="SOCIAL_POST"
          targetId={detail.postId}
          titleKey="travel.safety.reportPost"
          onClose={() => setReportOpen(false)}
        />
      ) : null}
      <div id="comments" ref={commentsRef} tabIndex={-1} aria-label={socialChrome.comments}><SocialPostComments
        postId={detail.postId}
        onCommentCreated={() => setDetail((current) => ({ ...current, engagement: { ...current.engagement, commentCount: current.engagement.commentCount + 1 } }))}
        onCommentDeleted={() => setDetail((current) => ({ ...current, engagement: { ...current.engagement, commentCount: Math.max(0, current.engagement.commentCount - 1) } }))}
      /></div>
    </main>
    <aside className="social-detail-rail"><strong>{socialChrome.keepExploring}</strong><p>{socialChrome.keepExploringHint}</p><Link to={backTo}>{backLabel}</Link></aside>
  </div>;
}
